// ═══════════════════════════════════════════════════════════════
// MÓDULO: Beneficios Fiscales (ISR - DGII República Dominicana)
// Gestiona gastos educativos, intereses hipotecarios y
// dependientes adicionales que afectan el cálculo de ISR.
// ═══════════════════════════════════════════════════════════════

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  GraduationCap, Home, Plus, Search, Edit2, Trash2,
  CheckCircle, XCircle, AlertCircle, RefreshCw, ChevronDown, X, Save
} from 'lucide-react';
import { useCompany } from '../../../contexts/CompanyContext';
import useEmployees from '../../../hooks/useEmployees';
import {
  getTaxBenefits,
  createTaxBenefit,
  updateTaxBenefit,
  deleteTaxBenefit
} from '../../../api/taxBenefits';
import Pagination from '../../common/Pagination';

const PAGE_SIZE = 15;

// ── Configuración de tipos ────────────────────────────────────────
const BENEFIT_TYPES = [
  {
    value:       'mortgage_interest',
    label:       'Intereses Hipotecarios',
    description: 'Reduce la base imponible. Cert. bancaria requerida.',
    effect:      'Reducción de base imponible',
    effectCls:   'text-blue-600 bg-blue-50',
    Icon:        Home,
    iconBg:      'bg-blue-100',
    iconCls:     'text-blue-600'
  },
  {
    value:       'education_expense',
    label:       'Gastos Educativos',
    description: 'Crédito fiscal del 20% del gasto. Aprobado por DGII.',
    effect:      'Crédito contra ISR',
    effectCls:   'text-green-600 bg-green-50',
    Icon:        GraduationCap,
    iconBg:      'bg-green-100',
    iconCls:     'text-green-600'
  }
];

const typeMap = Object.fromEntries(BENEFIT_TYPES.map(t => [t.value, t]));

const fmt = (n) =>
  parseFloat(n || 0).toLocaleString('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const currentYear = new Date().getFullYear();
const YEARS = Array.from({ length: 6 }, (_, i) => currentYear - i);

// ── StatCard ──────────────────────────────────────────────────────
function StatCard({ Icon, iconBg, iconCls, label, value, sub }) {
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs text-gray-500 mb-1">{label}</p>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
          {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
        </div>
        <div className={`w-10 h-10 ${iconBg} rounded-lg flex items-center justify-center`}>
          <Icon className={`w-5 h-5 ${iconCls}`} />
        </div>
      </div>
    </div>
  );
}

// ── Formulario modal ──────────────────────────────────────────────
function BenefitModal({ benefit, employees, onSave, onClose }) {
  const editing = !!benefit;
  const [form, setForm] = useState({
    employee_id:     benefit?.employee_id     || '',
    benefit_type:    benefit?.benefit_type    || 'mortgage_interest',
    annual_amount:   benefit?.annual_amount   || '',
    fiscal_year:     benefit?.fiscal_year     || currentYear,
    status:          benefit?.status          || 'active',
    source_document: benefit?.source_document || '',
    notes:           benefit?.notes           || '',
    periodicidad:    benefit?.periodicidad    || 'anual',
    mes_aplicacion:  benefit?.mes_aplicacion  ?? 12
  });
  const [saving, setSaving]   = useState(false);
  const [error,  setError]    = useState(null);

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.employee_id) return setError('Selecciona un empleado');
    if (!form.annual_amount || parseFloat(form.annual_amount) < 0)
      return setError('Ingresa un monto anual válido');

    setSaving(true);
    setError(null);
    try {
      const payload = {
        ...form,
        annual_amount: parseFloat(form.annual_amount),
        fiscal_year:   parseInt(form.fiscal_year, 10)
      };
      if (editing) {
        await updateTaxBenefit(benefit.id, payload);
      } else {
        await createTaxBenefit(payload);
      }
      onSave();
    } catch (err) {
      setError(err.response?.data?.error || 'Error al guardar beneficio');
    } finally {
      setSaving(false);
    }
  };

  const selectedType = typeMap[form.benefit_type];

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl border border-gray-200 w-full max-w-lg shadow-xl">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h3 className="text-sm font-semibold text-gray-900">
            {editing ? 'Editar Beneficio Fiscal' : 'Nuevo Beneficio Fiscal'}
          </h3>
          <button onClick={onClose} className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600">
            <X size={16} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">

          {/* Empleado */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Empleado *</label>
            <div className="relative">
              <select
                value={form.employee_id}
                onChange={e => set('employee_id', e.target.value)}
                disabled={editing}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400 bg-white appearance-none disabled:bg-gray-50 disabled:text-gray-400"
              >
                <option value="">Selecciona un empleado...</option>
                {employees.map(emp => (
                  <option key={emp.id} value={emp.id}>
                    {emp.nombre} {emp.apellido} — {emp.employee_id}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-2.5 w-4 h-4 text-gray-400 pointer-events-none" />
            </div>
          </div>

          {/* Tipo de beneficio */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Tipo de Beneficio *</label>
            <div className="grid grid-cols-1 gap-2">
              {BENEFIT_TYPES.map(t => (
                <label
                  key={t.value}
                  className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                    form.benefit_type === t.value
                      ? 'border-blue-400 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <input
                    type="radio"
                    name="benefit_type"
                    value={t.value}
                    checked={form.benefit_type === t.value}
                    onChange={() => set('benefit_type', t.value)}
                    className="accent-blue-600"
                  />
                  <div className={`w-8 h-8 ${t.iconBg} rounded-lg flex items-center justify-center flex-shrink-0`}>
                    <t.Icon className={`w-4 h-4 ${t.iconCls}`} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-gray-900">{t.label}</p>
                    <p className="text-xs text-gray-500">{t.description}</p>
                  </div>
                  <span className={`ml-auto text-xs font-medium px-2 py-0.5 rounded-full flex-shrink-0 ${t.effectCls}`}>
                    {t.effect}
                  </span>
                </label>
              ))}
            </div>
          </div>

          {/* Periodicidad */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Aplicación del Beneficio</label>
            <div className="flex gap-3">
              {[
                { value: 'anual',   label: 'Anual (una vez)',  desc: 'Aplica el monto completo en un mes específico' },
                { value: 'mensual', label: 'Mensual (prorrateado)', desc: 'Divide el total entre 12 y aplica cada mes' }
              ].map(p => (
                <label key={p.value} className={`flex-1 flex items-start gap-2 px-3 py-2 border rounded-lg cursor-pointer text-sm transition-colors ${
                  form.periodicidad === p.value ? 'border-blue-400 bg-blue-50 text-blue-800' : 'border-gray-200 text-gray-600 hover:border-gray-300'
                }`}>
                  <input
                    type="radio"
                    name="periodicidad"
                    value={p.value}
                    checked={form.periodicidad === p.value}
                    onChange={() => set('periodicidad', p.value)}
                    className="accent-blue-600 mt-0.5"
                  />
                  <div>
                    <p className="font-semibold text-xs">{p.label}</p>
                    <p className="text-xs text-gray-400">{p.desc}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Mes de aplicación (solo para anual) */}
          {form.periodicidad === 'anual' && (
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Mes de Aplicación *
              </label>
              <div className="relative">
                <select
                  value={form.mes_aplicacion}
                  onChange={e => set('mes_aplicacion', parseInt(e.target.value, 10))}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400 bg-white appearance-none"
                >
                  {[
                    { v: 1,  l: 'Enero' },
                    { v: 2,  l: 'Febrero' },
                    { v: 3,  l: 'Marzo' },
                    { v: 4,  l: 'Abril' },
                    { v: 5,  l: 'Mayo' },
                    { v: 6,  l: 'Junio' },
                    { v: 7,  l: 'Julio' },
                    { v: 8,  l: 'Agosto' },
                    { v: 9,  l: 'Septiembre' },
                    { v: 10, l: 'Octubre' },
                    { v: 11, l: 'Noviembre' },
                    { v: 12, l: 'Diciembre ★ (recomendado)' },
                  ].map(m => (
                    <option key={m.v} value={m.v}>{m.l}</option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-2.5 w-4 h-4 text-gray-400 pointer-events-none" />
              </div>
              <p className="text-xs text-gray-400 mt-1">
                {form.mes_aplicacion === 12
                  ? '✔ Diciembre es el cierre del año fiscal (RD). Plazo IR-17: 10 de enero.'
                  : form.mes_aplicacion >= 10
                  ? '⚠ Aplicar en este mes es válido. Asegúrate de tener la documentación DGII antes del 10 del mes siguiente.'
                  : '⚠ Aplicar antes de octubre es inusual. Confirma que el empleado proveyó los documentos de soporte (DGII).'}
              </p>
            </div>
          )}

          {/* Monto + año fiscal */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Monto Anual Aprobado *
              </label>
              <div className="relative">
                <span className="absolute left-3 top-2 text-sm text-gray-400">RD$</span>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.annual_amount}
                  onChange={e => set('annual_amount', e.target.value)}
                  placeholder="0.00"
                  className="w-full pl-10 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400"
                />
              </div>
              {form.annual_amount && (
                <p className="text-xs text-gray-400 mt-1">
                  {form.periodicidad === 'mensual'
                    ? `≈ RD$${fmt(parseFloat(form.annual_amount || 0) / 12)}/mes`
                    : `RD$${fmt(parseFloat(form.annual_amount || 0))} aplicado en el mes seleccionado`}
                </p>
              )}
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Año Fiscal *</label>
              <div className="relative">
                <select
                  value={form.fiscal_year}
                  onChange={e => set('fiscal_year', e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400 bg-white appearance-none"
                >
                  {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
                </select>
                <ChevronDown className="absolute right-3 top-2.5 w-4 h-4 text-gray-400 pointer-events-none" />
              </div>
            </div>
          </div>

          {/* Documento de soporte */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Documento de Soporte
              <span className="text-gray-400 font-normal ml-1">(resolución DGII, cert. bancaria…)</span>
            </label>
            <input
              type="text"
              value={form.source_document}
              onChange={e => set('source_document', e.target.value)}
              placeholder="Ej: DGII-2025-00123"
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400"
            />
          </div>

          {/* Estado */}
          <div className="flex items-center gap-3">
            <label className="text-xs font-medium text-gray-700">Estado:</label>
            <button
              type="button"
              onClick={() => set('status', form.status === 'active' ? 'inactive' : 'active')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                form.status === 'active'
                  ? 'bg-green-100 text-green-700'
                  : 'bg-gray-100 text-gray-600'
              }`}
            >
              {form.status === 'active'
                ? <><CheckCircle size={13} /> Activo</>
                : <><XCircle size={13} /> Inactivo</>}
            </button>
          </div>

          {error && (
            <div className="flex items-center gap-2 px-3 py-2 bg-red-50 border border-red-200 rounded-lg">
              <AlertCircle size={14} className="text-red-500 flex-shrink-0" />
              <p className="text-xs text-red-700">{error}</p>
            </div>
          )}

          {/* Acciones */}
          <div className="flex items-center justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-40"
            >
              <Save size={14} />
              {saving ? 'Guardando...' : editing ? 'Actualizar' : 'Guardar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
// COMPONENTE PRINCIPAL
// ══════════════════════════════════════════════════════════════════
export default function TaxBenefitsModule() {
  const { currentCompany } = useCompany();
  const { employees } = useEmployees();

  const [benefits,   setBenefits]   = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error,      setError]      = useState(null);

  const [showModal,    setShowModal]    = useState(false);
  const [editingItem,  setEditingItem]  = useState(null);
  const [page,         setPage]         = useState(1);

  // Filtros
  const [filterEmployee,    setFilterEmployee]    = useState('');
  const [filterType,        setFilterType]        = useState('');
  const [filterYear,        setFilterYear]        = useState(String(currentYear));
  const [filterStatus,      setFilterStatus]      = useState('active');
  const [searchText,        setSearchText]        = useState('');

  const loadBenefits = useCallback(async (silent = false) => {
    if (!currentCompany) return;
    if (!silent) setLoading(true);
    else         setRefreshing(true);
    setError(null);
    try {
      const data = await getTaxBenefits({
        fiscalYear: filterYear || undefined,
        status:     filterStatus || undefined
      });
      setBenefits(data.benefits || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Error al cargar beneficios');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [currentCompany, filterYear, filterStatus]);

  useEffect(() => { loadBenefits(); }, [loadBenefits]);

  // Filtrado local (empleado, tipo, texto)
  const filtered = useMemo(() => {
    setPage(1);
    let list = benefits;
    if (filterEmployee) list = list.filter(b => b.employee_id === filterEmployee);
    if (filterType)     list = list.filter(b => b.benefit_type === filterType);
    if (searchText.trim()) {
      const q = searchText.toLowerCase();
      list = list.filter(b =>
        (`${b.empleado_nombre} ${b.empleado_apellido}`).toLowerCase().includes(q) ||
        (b.empleado_codigo || '').toLowerCase().includes(q) ||
        (b.source_document || '').toLowerCase().includes(q)
      );
    }
    return list;
  }, [benefits, filterEmployee, filterType, searchText]);

  const totalPagesBenefits = Math.ceil(filtered.length / PAGE_SIZE);
  const pagedBenefits = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // Totales por tipo
  const totals = useMemo(() => {
    const t = { mortgage_interest: 0, education_expense: 0 };
    for (const b of filtered.filter(b => b.status === 'active')) {
      if (t[b.benefit_type] !== undefined) t[b.benefit_type] += parseFloat(b.annual_amount || 0);
    }
    return t;
  }, [filtered]);

  const handleNew    = ()    => { setEditingItem(null); setShowModal(true); };
  const handleEdit   = (b)   => { setEditingItem(b);   setShowModal(true); };
  const handleDelete = async (b) => {
    const empName = `${b.empleado_nombre} ${b.empleado_apellido}`;
    const typeLabel = typeMap[b.benefit_type]?.label || b.benefit_type;
    if (!window.confirm(`¿Eliminar "${typeLabel}" de ${empName} (${b.fiscal_year})?`)) return;
    try {
      await deleteTaxBenefit(b.id);
      await loadBenefits(true);
    } catch (err) {
      alert(err.response?.data?.error || 'Error al eliminar');
    }
  };

  const handleSaved  = async () => {
    setShowModal(false);
    await loadBenefits(true);
  };

  // ── Loading ───────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto mb-3" />
          <p className="text-sm text-gray-400">Cargando beneficios fiscales...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">

      {/* ── Stat cards ──────────────────────────────────────────── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {BENEFIT_TYPES.map(t => (
          <StatCard
            key={t.value}
            Icon={t.Icon}
            iconBg={t.iconBg}
            iconCls={t.iconCls}
            label={t.label}
            value={`RD$${fmt(totals[t.value])}`}
            sub={`≈ RD$${fmt(totals[t.value] / 12)}/mes · ${
              filtered.filter(b => b.benefit_type === t.value && b.status === 'active').length
            } beneficio(s)`}
          />
        ))}
      </div>

      {/* ── Toolbar ─────────────────────────────────────────────── */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="flex flex-wrap items-center gap-3">

          {/* Búsqueda texto */}
          <div className="relative flex-1 min-w-[180px]">
            <Search className="absolute left-3 top-2.5 w-3.5 h-3.5 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar empleado, código, documento..."
              value={searchText}
              onChange={e => setSearchText(e.target.value)}
              className="w-full pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400"
            />
          </div>

          {/* Filtro tipo */}
          <div className="relative">
            <select
              value={filterType}
              onChange={e => setFilterType(e.target.value)}
              className="px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400 bg-white appearance-none pr-8"
            >
              <option value="">Todos los tipos</option>
              {BENEFIT_TYPES.map(t => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
            <ChevronDown className="absolute right-2 top-2.5 w-4 h-4 text-gray-400 pointer-events-none" />
          </div>

          {/* Filtro empleado */}
          <div className="relative">
            <select
              value={filterEmployee}
              onChange={e => setFilterEmployee(e.target.value)}
              className="px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400 bg-white appearance-none pr-8"
            >
              <option value="">Todos los empleados</option>
              {employees.map(emp => (
                <option key={emp.id} value={emp.id}>
                  {emp.nombre} {emp.apellido}
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-2 top-2.5 w-4 h-4 text-gray-400 pointer-events-none" />
          </div>

          {/* Filtro año */}
          <div className="relative">
            <select
              value={filterYear}
              onChange={e => setFilterYear(e.target.value)}
              className="px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400 bg-white appearance-none pr-8"
            >
              <option value="">Todos los años</option>
              {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
            <ChevronDown className="absolute right-2 top-2.5 w-4 h-4 text-gray-400 pointer-events-none" />
          </div>

          {/* Filtro estado */}
          <div className="relative">
            <select
              value={filterStatus}
              onChange={e => setFilterStatus(e.target.value)}
              className="px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400 bg-white appearance-none pr-8"
            >
              <option value="">Todos los estados</option>
              <option value="active">Activos</option>
              <option value="inactive">Inactivos</option>
            </select>
            <ChevronDown className="absolute right-2 top-2.5 w-4 h-4 text-gray-400 pointer-events-none" />
          </div>

          {/* Pill contador */}
          <span className="text-xs text-gray-500 bg-gray-100 px-2.5 py-1 rounded-full font-medium">
            <span className="font-semibold text-gray-700">{filtered.length}</span> registro{filtered.length !== 1 ? 's' : ''}
          </span>

          {/* Refresh */}
          <button
            onClick={() => loadBenefits(true)}
            disabled={refreshing}
            className="p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-40"
            title="Actualizar"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          </button>

          {/* Nuevo beneficio */}
          <button
            onClick={handleNew}
            className="ml-auto flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus size={14} />
            Nuevo Beneficio
          </button>
        </div>

        {error && (
          <div className="flex items-center gap-2 mt-3 px-3 py-2 bg-red-50 border border-red-200 rounded-lg">
            <AlertCircle size={14} className="text-red-500 flex-shrink-0" />
            <p className="text-xs text-red-700">{error}</p>
          </div>
        )}
      </div>

      {/* ── Tabla ───────────────────────────────────────────────── */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        {filtered.length === 0 ? (
          <div className="py-16 text-center">
            <GraduationCap className="w-12 h-12 text-gray-200 mx-auto mb-3" />
            <p className="text-sm font-medium text-gray-500 mb-1">No hay beneficios fiscales registrados</p>
            <p className="text-xs text-gray-400">
              {searchText || filterEmployee || filterType
                ? 'Ajusta los filtros de búsqueda'
                : 'Crea el primer beneficio fiscal con el botón "Nuevo Beneficio"'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-blue-600 text-white">
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider">Empleado</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider">Tipo</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider">Año</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider">Monto Anual</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider">Mensual</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider">Efecto ISR</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider">Documento</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider">Estado</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {pagedBenefits.map(b => {
                  const t = typeMap[b.benefit_type] || {};
                  const monthly = parseFloat(b.annual_amount || 0) / 12;
                  return (
                    <tr key={b.id} className="hover:bg-gray-50 transition-colors">

                      {/* Empleado */}
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          {b.empleado_foto ? (
                            <img src={b.empleado_foto} alt="" className="w-7 h-7 rounded-full object-cover flex-shrink-0" />
                          ) : (
                            <div className="w-7 h-7 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                              <span className="text-xs font-bold text-blue-600">
                                {b.empleado_nombre?.[0]?.toUpperCase()}
                              </span>
                            </div>
                          )}
                          <div>
                            <p className="font-semibold text-gray-900 text-sm">
                              {b.empleado_nombre} {b.empleado_apellido}
                            </p>
                            <p className="text-xs text-gray-400">{b.empleado_codigo}</p>
                          </div>
                        </div>
                      </td>

                      {/* Tipo */}
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          {t.Icon && (
                            <div className={`w-7 h-7 ${t.iconBg} rounded-lg flex items-center justify-center flex-shrink-0`}>
                              <t.Icon className={`w-3.5 h-3.5 ${t.iconCls}`} />
                            </div>
                          )}
                          <span className="text-sm font-medium text-gray-700">{t.label || b.benefit_type}</span>
                        </div>
                      </td>

                      {/* Año */}
                      <td className="px-4 py-3">
                        <span className="text-sm font-semibold text-gray-900">{b.fiscal_year}</span>
                      </td>

                      {/* Monto anual */}
                      <td className="px-4 py-3 text-right">
                        <span className="text-sm font-semibold text-gray-900">RD${fmt(b.annual_amount)}</span>
                      </td>

                      {/* Mensual */}
                      <td className="px-4 py-3 text-right">
                        <span className="text-xs text-gray-500">RD${fmt(monthly)}</span>
                      </td>

                      {/* Efecto ISR */}
                      <td className="px-4 py-3">
                        <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${t.effectCls || 'bg-gray-100 text-gray-600'}`}>
                          {t.effect || '—'}
                        </span>
                      </td>

                      {/* Documento */}
                      <td className="px-4 py-3">
                        <span className="text-xs text-gray-500 truncate max-w-[120px] block">
                          {b.source_document || <span className="text-gray-300">—</span>}
                        </span>
                      </td>

                      {/* Estado */}
                      <td className="px-4 py-3 text-center">
                        {b.status === 'active' ? (
                          <span className="inline-flex items-center gap-1 text-xs font-semibold text-green-700 bg-green-50 px-2 py-0.5 rounded-full">
                            <CheckCircle size={11} /> Activo
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-xs font-semibold text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                            <XCircle size={11} /> Inactivo
                          </span>
                        )}
                      </td>

                      {/* Acciones */}
                      <td className="px-4 py-3 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <button
                            onClick={() => handleEdit(b)}
                            className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          >
                            <Edit2 size={12} /> Editar
                          </button>
                          <button
                            onClick={() => handleDelete(b)}
                            className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                          >
                            <Trash2 size={12} /> Eliminar
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot className="bg-gray-50 border-t border-gray-200">
                <tr>
                  <td colSpan="3" className="px-4 py-3 text-xs text-gray-500">
                    <span className="font-semibold text-gray-700">{filtered.length}</span> beneficio{filtered.length !== 1 ? 's' : ''} mostrado{filtered.length !== 1 ? 's' : ''}
                  </td>
                  <td className="px-4 py-3 text-right text-xs font-semibold text-gray-700">
                    RD${fmt(filtered.reduce((s, b) => s + parseFloat(b.annual_amount || 0), 0))}
                  </td>
                  <td className="px-4 py-3 text-right text-xs text-gray-500">
                    RD${fmt(filtered.reduce((s, b) => s + parseFloat(b.annual_amount || 0), 0) / 12)}
                  </td>
                  <td colSpan="4" />
                </tr>
              </tfoot>
            </table>
          <Pagination
            page={page}
            totalPages={totalPagesBenefits}
            total={filtered.length}
            pageSize={PAGE_SIZE}
            onPageChange={setPage}
          />
          </div>
        )}
      </div>

      {/* ── Modal ───────────────────────────────────────────────── */}
      {showModal && (
        <BenefitModal
          benefit={editingItem}
          employees={employees.filter(e => e.estado === 'activo')}
          onSave={handleSaved}
          onClose={() => setShowModal(false)}
        />
      )}
    </div>
  );
}
